<script setup>
import FormInput from '@/components/Shared/Form/Input/Index.vue';
import FormButton from '@/components/Shared/Form/Button.vue';
import CustomersTable from '@/Pages/PartnerSupport/Tickets/Partials/CustomersTable.vue';
import {useForm} from '@inertiajs/vue3';
import FormEditor from '@/components/Shared/Form/Editor/Index.vue';
import {ref} from 'vue';
import BillingInvoiceHistory from '@/Pages/Dashboard/Account/Partials/Billing/InvoiceHistory.vue';
import Swal from "sweetalert2";

const props = defineProps({
  ticket: {
    type: Object,
    required: true,
  },
  statusOptions: {
    type: Array,
    required: true,
  },
  classificationOptions: {
    type: Array,
    required: true,
  },
  partnerSupports: {
    type: Array,
    required: true,
  },
  usersAffected: {
    type: Object,
    required: true,
  },
  invoices: {
    type: Array,
    required: true,
  },
});

const editor = ref();
const createGitlabIssueButtonVisible = ref(!props.ticket.gitlab_link);
const form = useForm({
  assignee: props.ticket.assigneeValue,
  status: props.ticket.statusValue,
  classification: props.ticket.classificationValue,
  gitlab_link: props.ticket.gitlab_link,
});

const contactForm = useForm({
  admin_notes: props.ticket.user.adminNotes,
  received_free_installation_call: props.ticket.user.receivedFreeInstallationCall,
});

const saveForm = () => {
  form
    .transform(data => ({
      ...data,
      status: data.status?.id,
      classification: data.classification?.id,
      assignee: data.assignee?.id,
    }))
    .patch(route('partner-support.tickets.update', {ticket: props.ticket.id}));
};

const createGitlabIssue = () => {
  return axios
    .post(route('partner-support.tickets.create-gitlab-issue', {ticket: props.ticket.id}))
    .then(({data}) => {
      form.gitlab_link = data;
      createGitlabIssueButtonVisible.value = false
    }).catch((data) => {
      Swal.fire({
        text: data.response.data.message,
        icon: 'error',
        showConfirmButton: false,
        timer: 3000,
      });
    })
};

const saveContactForm = () => {
  contactForm.patch(route('partner-support.tickets.update-contact-form', {ticket: props.ticket.id}));
};
</script>

<script>
import DashboardLayout from '@/Layouts/DashboardLayout.vue';
import SupportTicketLayout from '@/Layouts/SupportTicketLayout.vue';

export default {
  layout: [DashboardLayout, SupportTicketLayout],
};
</script>

<template>
  <div>
    <div class="tile">
      <div>
        <form-input
          id="type-question"
          :label-text="trans('__JSON__.Type of request')"
          :model-value="ticket.requestType"
          container-class="row-span-2"
          readonly
          type="text"
        />
        <form-input
          id="subject"
          :label-text="trans('__JSON__.Subject')"
          :model-value="ticket.subject"
          container-class="row-span-2"
          readonly
          type="text"
        />
      </div>
      <div class="grid-2 gap">
        <form-input
          id="application1"
          :label-text="trans('common.application') + ' 1'"
          :model-value="ticket.application1?.name ?? 'N/A'"
          container-class="row-span-2"
          readonly
          type="text"
        />

        <form-input
          id="application2"
          :label-text="trans('common.application') + ' 2'"
          :model-value="ticket.application2?.name ?? 'N/A'"
          container-class="row-span-2"
          readonly
          type="text"
        />
      </div>
      <div class="flex justify-content-start my-3">
        <form-input
          id="hasWorkaroundAvailable"
          :hover-help-text="trans('__JSON__.Is there a workaround available or does this block the complete flow?')"
          :label-text="trans('__JSON__.Has workaround available')"
          :model-value="ticket.hasWorkaroundAvailable"
          disabled
          readonly
          type="checkbox"
        />
        <span :class="[ticket.hasWorkaroundAvailable ? 'text-success' : 'text-danger']" class="font-bold uppercase">{{
            ticket.hasWorkaroundAvailable ? trans('__JSON__.Yes') : trans('__JSON__.No')
          }}</span>
      </div>
      <div class="grid-3 gap">
        <form-input
          id="impact"
          :label-text="trans('__JSON__.Impact')"
          :model-value="ticket.impact"
          container-class="row-span-2"
          readonly
          type="text"
        />
        <form-input
          id="urgency"
          :label-text="trans('__JSON__.Urgency')"
          :model-value="ticket.urgency"
          container-class="row-span-2"
          readonly
          type="text"
        />
        <form-input
          id="priority"
          :label-text="trans('__JSON__.Priority')"
          :model-value="ticket.priority"
          container-class="row-span-2"
          readonly
          type="text"
        />
      </div>
      <div class="grid-3 gap">
        <form-input
          id="status"
          v-model="form.status"
          :error="form.errors['status.id']"
          :label-text="trans('__JSON__.Status')"
          :options="statusOptions"
          :placeholder="trans('__JSON__.Select Status')"
          label="name"
          track-by="id"
          type="select"
        />
        <form-input
          id="users"
          v-model="form.assignee"
          :error="form.errors['assignee.id']"
          :label-text="trans('__JSON__.Assignee')"
          :options="partnerSupports"
          :placeholder="trans('__JSON__.Select assignee')"
          label="name"
          track-by="id"
          type="select"
        />
        <form-input
          id="users"
          v-model="form.classification"
          :error="form.errors['classification.id']"
          :label-text="trans('__JSON__.Classification')"
          :options="classificationOptions"
          :placeholder="trans('__JSON__.Select classification')"
          :allowEmpty="false"
          :deselect-label="trans('__JSON__.Can\'t remove this value')"
          label="name"
          track-by="id"
          type="select"
        />
      </div>
      <div class="flex">
        <form-input
          id="gitlab-link"
          v-model="form.gitlab_link"
          :label-text="trans('__JSON__.Gitlab Link')"
          type="text"
          container-class="w-full"
        />
        <button v-show="createGitlabIssueButtonVisible"
                class="btn btn-success"
                style="height: 42px;
                margin-top: 38px"
                @click="createGitlabIssue">
          +
        </button>
      </div>
      <div class="d-flex justify-content-end mt-2">
        <form-button :processing="form.processing" :value="trans('common.update')" class="button" @click="saveForm"/>
      </div>
    </div>

    <div class="my-4 tile card">
      <div class="card-header border-bottom rounded-0">
        <h3 class="card-text">
          <span>{{ trans('__JSON__.Contact details') }}</span>
        </h3>
      </div>
      <div class="card-body">
        <form-input
          id="name"
          :label-text="trans('common.name')"
          :model-value="ticket.user.name"
          container-class="row-span-2"
          readonly
          type="text"
        />
        <form-input
          id="email"
          :label-text="trans('common.email')"
          :model-value="ticket.user.email"
          container-class="row-span-2"
          readonly
          type="text"
        />
        <form-input
          id="phone"
          :label-text="trans('common.phone_number')"
          :model-value="ticket.user.phone"
          container-class="row-span-2"
          readonly
          type="text"
        />
        <form-input
          id="receivedFreeInstallationCall"
          v-model="contactForm.received_free_installation_call"
          :label-text="trans('__JSON__.Received free installation call?')"
          container-class="row-span-2"
          type="checkbox"
        />
        <div class="my-2">
          <h5>{{ trans('__JSON__.Admin notes') }}</h5>
          <form-editor
            ref="editor"
            v-model="contactForm.admin_notes"
            :error="contactForm.errors.admin_notes"
            style="background: white"
          ></form-editor>
        </div>

        <div class="d-flex justify-content-end mt-2">
          <form-button
            :processing="contactForm.processing"
            :value="trans('common.update')"
            class="button"
            @click="saveContactForm"
          />
        </div>
      </div>
    </div>

    <billing-invoice-history :invoices="invoices || []" :ticket="ticket" hide-action show-description/>

    <div v-if="usersAffected.data.length > 0" class="my-4 tile card">
      <div class="card-header border-bottom rounded-0">
        <h3 class="card-text">
          <span>{{ trans('__JSON__.Customers affected') }}</span>
        </h3>
      </div>
      <div class="card-body">
        <customers-table :ticket="ticket" :users-affected="usersAffected"/>
      </div>
    </div>
  </div>
</template>
